/* Showroom Availability — snippets/showroom-availability.liquid
 *
 * Shows the selected variant's stock at the visitor's nearest showroom and
 * an "all showrooms" modal. Hidden when no showroom is near the visitor.
 * All location lookups live in this file and only run where the block renders.
 *
 * Location, first match wins:
 *  1. A postal code the shopper entered in this block (localStorage).
 *  2. Precise position, only if geolocation is ALREADY granted (never prompts).
 *  3. IP location from Floorstock (data-geo-url), once per session, 1.5s budget.
 *  4. Browser time zone -> provinces (America/Vancouver -> BC ...).
 * With coordinates, "nearby" = within data-radius-km; with only a province,
 * "nearby" = a showroom in that province.
 * ?showroom_tz=<IANA zone> skips 1-3, for testing.
 * ?showroom_debug logs the location and visibility decision to the console.
 */
(() => {
  if (customElements.get("showroom-availability")) return;

  // n > LOW_STOCK_MAX is "Available"; 1..LOW_STOCK_MAX is "Limited".
  const LOW_STOCK_MAX = 1;
  const GEO_TIMEOUT_MS = 1500;
  const STORE_KEY = "biktrix:showroom"; // store the shopper picked (localStorage)
  const POSTAL_KEY = "biktrix:showroom-postal"; // location from an entered postal code (localStorage)
  const IP_KEY = "biktrix:showroom-ip"; // IP lookup result for this session (sessionStorage)

  const ZONES = {
    "America/Vancouver": { regions: ["BC"], ref: [49.2827, -123.1207] },
    "America/Dawson_Creek": { regions: ["BC"], ref: [55.7596, -120.2377] },
    "America/Fort_Nelson": { regions: ["BC"], ref: [58.8053, -122.6972] },
    "America/Creston": { regions: ["BC"], ref: [49.0955, -116.5135] },
    "America/Edmonton": { regions: ["AB"], ref: [51.0447, -114.0719] },
    "America/Regina": { regions: ["SK"], ref: [50.4452, -104.6189] },
    "America/Swift_Current": { regions: ["SK"], ref: [50.2881, -107.7939] },
    "America/Winnipeg": { regions: ["MB"], ref: [49.8951, -97.1384] },
    "America/Toronto": { regions: ["ON", "QC"], ref: [43.6532, -79.3832] },
    "America/Montreal": { regions: ["QC"], ref: [45.5017, -73.5673] },
    "America/Halifax": { regions: ["NS", "PE"], ref: [44.6488, -63.5752] },
    "America/Moncton": { regions: ["NB"], ref: [46.0878, -64.7782] },
    "America/St_Johns": { regions: ["NL"], ref: [47.5615, -52.7126] },
  };

  // First letter of a Canadian postal code -> province.
  const POSTAL_REGIONS = {
    A: "NL", B: "NS", C: "PE", E: "NB", G: "QC", H: "QC", J: "QC",
    K: "ON", L: "ON", M: "ON", N: "ON", P: "ON", R: "MB", S: "SK",
    T: "AB", V: "BC", X: "NT", Y: "YT",
  };
  const POSTAL_PATTERN = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z](\d[ABCEGHJ-NPRSTV-Z]\d)?$/;

  const STATUS = {
    in: {
      label: "Available",
      detail: "In stock at this showroom. Visit for a test ride.",
    },
    low: {
      label: "Limited availability",
      detail: "Only 1 unit left. Check with the showroom before visiting.",
    },
    out: {
      label: "Not available",
      detail: "Not in stock at this showroom. Order online or try another location.",
    },
  };

  const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

  const telHref = (phone) => "tel:" + String(phone || "").replace(/[^\d+]/g, "");

  const statusOf = (store) => {
    const qty = Number(store.qty) || 0;
    if (qty > LOW_STOCK_MAX) return "in";
    return qty > 0 ? "low" : "out";
  };

  const distanceKm = (a, b) => {
    const rad = (d) => (d * Math.PI) / 180;
    const dLat = rad(b[0] - a[0]);
    const dLng = rad(b[1] - a[1]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
    return 6371 * 2 * Math.asin(Math.sqrt(h));
  };

  const storage = (type) => {
    try {
      return window[type];
    } catch (e) {
      return null;
    }
  };
  const read = (type, key) => {
    try {
      return storage(type).getItem(key);
    } catch (e) {
      return null;
    }
  };
  const write = (type, key, value) => {
    try {
      if (value === null) storage(type).removeItem(key);
      else storage(type).setItem(key, value);
    } catch (e) {}
  };
  const readJson = (type, key) => {
    try {
      return JSON.parse(read(type, key));
    } catch (e) {
      return null;
    }
  };

  const hasPoint = (v) => v && typeof v.lat === "number" && typeof v.lng === "number";

  // Normalised visitor: { point: [lat, lng] | null, regions: [...], useRadius, source, label }
  const visitorFrom = (loc, source) => {
    if (!loc) return null;
    const point = hasPoint(loc) ? [loc.lat, loc.lng] : null;
    const regions = loc.region ? [loc.region] : loc.regions || [];
    if (!point && !regions.length) return null;
    return { point, regions, useRadius: Boolean(point), source, label: loc.label || "" };
  };

  const fetchJson = async (url) => {
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const timer = controller && setTimeout(() => controller.abort(), GEO_TIMEOUT_MS);
    try {
      const response = await fetch(url, { signal: controller && controller.signal, credentials: "omit" });
      return response.ok ? await response.json() : null;
    } catch (e) {
      return null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  const DEBUG = new URLSearchParams(window.location.search).has("showroom_debug");
  const debug = (...args) => {
    if (DEBUG) console.info("[showroom-availability]", ...args);
  };

  // Resolves to null if the promise hasn't settled in time. Firefox can leave
  // getCurrentPosition unanswered (e.g. macOS location services off).
  const withTimeout = (promise, ms) =>
    Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

  const withParam = (url, key, value) => url + (url.includes("?") ? "&" : "?") + key + "=" + encodeURIComponent(value);

  const fromZone = (zone) => {
    const match = ZONES[zone];
    return match ? { point: match.ref, regions: match.regions, useRadius: false, source: "timezone", label: "" } : null;
  };

  // One lookup per page, shared by every instance. knownVisitor holds the
  // result once settled so a re-rendered block can draw synchronously.
  let locating = null;
  let knownVisitor; // undefined until the lookup settles; null = unknown location
  let pagePick = null; // showroom picked in the dropdown; kept across buy-box re-renders
  const locateVisitor = (geoUrl) => {
    if (locating) return locating;
    locating = (async () => {
      const override = new URLSearchParams(window.location.search).get("showroom_tz");
      if (override) {
        debug("time zone override", override);
        return fromZone(override);
      }

      const postal = visitorFrom(readJson("localStorage", POSTAL_KEY), "postal");
      if (postal) {
        debug("using entered postal code", postal);
        return postal;
      }

      if (navigator.permissions && navigator.geolocation) {
        try {
          const permission = await withTimeout(navigator.permissions.query({ name: "geolocation" }), 1000);
          debug("geolocation permission", permission ? permission.state : "no answer");
          if (permission && permission.state === "granted") {
            const pos = await withTimeout(
              new Promise((resolve, reject) =>
                navigator.geolocation.getCurrentPosition(resolve, reject, { maximumAge: 3600000, timeout: 3000 }),
              ),
              3500,
            );
            debug("device position", pos ? "ok" : "no answer");
            if (pos) return visitorFrom({ lat: pos.coords.latitude, lng: pos.coords.longitude }, "device");
          }
        } catch (e) {
          debug("device position failed", e && e.message);
        }
      }

      if (geoUrl) {
        // Only a usable result is cached; a failed or empty lookup retries next page.
        let fromIp = visitorFrom(readJson("sessionStorage", IP_KEY), "ip");
        if (!fromIp) {
          const ip = await fetchJson(geoUrl);
          debug("IP lookup", ip || "failed or blocked");
          fromIp = visitorFrom(ip, "ip");
          write("sessionStorage", IP_KEY, fromIp ? JSON.stringify(ip) : null);
        }
        if (fromIp) return fromIp;
      }

      let zone = null;
      try {
        zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (e) {}
      debug("time zone fallback", zone);
      return fromZone(zone);
    })().then((visitor) => {
      knownVisitor = visitor;
      return visitor;
    });
    return locating;
  };

  // The theme swaps the buy box's HTML on every variant change; a stylesheet
  // link inside it would be removed and re-added each time (a flash of
  // unstyled block). Keep one copy in <head> and drop the rest.
  const hoistStylesheet = () => {
    const links = document.querySelectorAll('link[rel="stylesheet"][href*="showroom-availability.css"]');
    const inHead = [...links].find((l) => l.parentNode === document.head);
    links.forEach((link) => {
      if (link === inHead) return;
      if (inHead) link.remove();
      else document.head.appendChild(link);
    });
  };

  class ShowroomAvailability extends HTMLElement {
    connectedCallback() {
      hoistStylesheet();
      this.cache = new Map();
      this.filter = "all";
      this.data = this.parse(this.querySelector("[data-showroom-json]"));
      debug("init", this.data ? `${(this.data.stores || []).length} showrooms, fresh=${this.data.fresh}` : "no data");
      if (!this.data) return;
      this.cache.set(String(this.data.variant), this.data);

      this.select = this.querySelector("[data-sa-select]");
      this.modal = this.querySelector("[data-sa-modal]");
      document.body.appendChild(this.modal);

      this.onVariantChanged = (event) => {
        const variant = event.detail && event.detail.variant;
        if (variant && variant.id) this.loadVariant(variant.id);
      };
      this.scope = this.closest("[data-section-id]") || document;
      this.scope.addEventListener("variant:changed", this.onVariantChanged);

      // A dropdown pick holds for this page only; the next visit picks again.
      write("localStorage", STORE_KEY, null); // clear picks saved by earlier versions
      this.select.addEventListener("change", () => {
        pagePick = this.select.value;
        this.render();
      });
      this.querySelector("[data-sa-open]").addEventListener("click", (e) => this.openModal(e.currentTarget));
      this.modal.addEventListener("click", (e) => {
        if (e.target === this.modal || e.target.closest("[data-sa-close]")) this.closeModal();
        const pill = e.target.closest("[data-sa-filter]");
        if (pill) {
          this.filter = pill.dataset.saFilter;
          this.renderModal();
        }
      });
      this.modal.addEventListener("keydown", (e) => this.onModalKeydown(e));

      const toggle = this.querySelector("[data-sa-postal-toggle]");
      this.postalForm = this.querySelector("[data-sa-postal-form]");
      toggle.addEventListener("click", () => {
        const open = this.postalForm.hidden;
        this.postalForm.hidden = !open;
        toggle.setAttribute("aria-expanded", String(open));
        if (open) this.querySelector("[data-sa-postal-input]").focus();
      });
      this.querySelector("[data-sa-postal-submit]").addEventListener("click", (e) => this.onPostalSubmit(e));
      // Enter would otherwise submit the surrounding add-to-cart form.
      this.querySelector("[data-sa-postal-input]").addEventListener("keydown", (e) => {
        if (e.key === "Enter") this.onPostalSubmit(e);
      });

      this.render();
    }

    // Look the visitor up only once there's showroom stock to show, so
    // products Floorstock doesn't sync (accessories) never call the geo URL.
    // Returns true when the location is already known, so render() can draw
    // in the same task (no hidden-then-shown flash after a buy-box re-render).
    ensureLocated() {
      if (this.data.fresh !== true) return false;
      if (knownVisitor !== undefined) {
        this.located = true;
        this.visitor = knownVisitor;
        return true;
      }
      if (this.locating) return false;
      this.locating = true;
      locateVisitor(this.dataset.geoUrl).then((visitor) => {
        this.located = true;
        this.visitor = visitor;
        this.render();
      });
      return false;
    }

    disconnectedCallback() {
      if (this.scope) this.scope.removeEventListener("variant:changed", this.onVariantChanged);
      if (this.modal && this.modal.parentNode === document.body) this.modal.remove();
    }

    parse(script) {
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return null;
      }
    }

    loadVariant(id) {
      const key = String(id);
      this.pendingVariant = key;
      if (this.cache.has(key)) {
        this.data = this.cache.get(key);
        this.render();
        return;
      }
      const root = (window.routes && window.routes.rootUrlWithoutSlash) || "";
      fetch(`${root}/variants/${key}?section_id=showroom-availability`)
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const data = this.parse(doc.querySelector("[data-showroom-json]"));
          if (!data) return;
          this.cache.set(key, data);
          // Ignore a slow response for a variant the shopper already left.
          if (this.pendingVariant !== key) return;
          this.data = data;
          this.render();
        })
        .catch(() => {});
    }

    async onPostalSubmit(e) {
      e.preventDefault();
      const input = this.querySelector("[data-sa-postal-input]");
      const error = this.querySelector("[data-sa-postal-error]");
      const code = input.value.toUpperCase().replace(/\s+/g, "");
      if (!POSTAL_PATTERN.test(code)) {
        error.textContent = "Enter a Canadian postal code, like V3S 1A1.";
        error.hidden = false;
        return;
      }
      error.hidden = true;

      // Only the first three characters (the area) ever leave the browser.
      const fsa = code.slice(0, 3);
      let loc = { region: POSTAL_REGIONS[fsa[0]], label: fsa };
      if (this.dataset.geoUrl) {
        const found = await fetchJson(withParam(this.dataset.geoUrl, "postal", fsa));
        if (hasPoint(found)) loc = { ...loc, lat: found.lat, lng: found.lng, region: found.region || loc.region };
      }
      write("localStorage", POSTAL_KEY, JSON.stringify(loc));
      pagePick = null; // re-pick for the new location
      this.visitor = knownVisitor = visitorFrom(loc, "postal");
      this.postalForm.hidden = true;
      this.querySelector("[data-sa-postal-toggle]").setAttribute("aria-expanded", "false");
      this.render();
    }

    // Showrooms with status and distance, nearby ones first.
    stores() {
      const visitor = this.visitor;
      const radius = Number(this.dataset.radiusKm) || 150;
      return (this.data.stores || [])
        .map((store) => {
          const km = visitor && visitor.point && hasPoint(store) ? distanceKm(visitor.point, [store.lat, store.lng]) : null;
          let nearby = false;
          if (visitor && visitor.useRadius) nearby = km !== null && km <= radius;
          else if (visitor) nearby = visitor.regions.includes(store.region);
          return { ...store, id: String(store.id), status: statusOf(store), km, nearby };
        })
        .sort((a, b) => {
          if (a.nearby !== b.nearby) return a.nearby ? -1 : 1;
          if (a.km !== null && b.km !== null) return a.km - b.km;
          return String(a.city).localeCompare(String(b.city));
        });
    }

    // The closest showroom if it has the variant; otherwise the closest one in
    // the same province that does; otherwise the closest showroom (shown as out).
    defaultStore(stores) {
      const nearest = stores[0];
      if (nearest.status !== "out") return nearest;
      const province = (this.visitor && this.visitor.regions[0]) || nearest.region;
      return stores.find((s) => s.region === province && s.status !== "out") || nearest;
    }

    variantLabel() {
      return String(this.data.variantTitle || "")
        .split(" / ")
        .join(" · ");
    }

    render() {
      if (!this.located && !this.ensureLocated()) return;
      const stores = this.stores();
      const anyNearby = stores.some((s) => s.nearby);
      const fromPostal = this.visitor && this.visitor.source === "postal";
      // Stale Floorstock data never shows. Otherwise a shopper who entered a
      // postal code keeps the block even with no showroom nearby.
      const visible = this.data.fresh === true && stores.length > 0 && (anyNearby || fromPostal);
      this.hidden = !visible;
      // The block stands in for the "Find Nearby Store" row while it's shown.
      const fallback = this.parentElement && this.parentElement.querySelector("[data-showroom-fallback]");
      if (fallback) fallback.hidden = visible;
      debug("decision", {
        visible,
        fresh: this.data.fresh,
        visitor: this.visitor,
        picked: pagePick || null,
        stores: stores.map((s) => `${s.city}: ${s.status}${s.km !== null ? `, ${Math.round(s.km)} km` : ""}${s.nearby ? ", nearby" : ""}`),
      });
      if (!visible) return;

      const selected = stores.find((s) => s.id === pagePick) || this.defaultStore(stores);
      this.storeId = selected.id;
      const status = STATUS[selected.status];

      const note = this.querySelector("[data-sa-note]");
      note.hidden = !(fromPostal && !anyNearby);
      note.textContent = note.hidden ? "" : `No showrooms near ${this.visitor.label}. Showing all locations.`;

      this.select.innerHTML = stores
        .map((s) => `<option value="${escapeHtml(s.id)}"${s.id === this.storeId ? " selected" : ""}>${escapeHtml(s.city)} Showroom</option>`)
        .join("");

      this.querySelector("[data-sa-variant]").textContent = this.variantLabel();
      this.querySelector("[data-sa-status]").dataset.status = selected.status;
      this.querySelector("[data-sa-label]").textContent = status.label;
      this.querySelector("[data-sa-detail]").textContent = status.detail;
      this.querySelector("[data-sa-address]").textContent = selected.address;

      const call = this.querySelector("[data-sa-call]");
      call.hidden = !(selected.status === "low" && selected.phone);
      if (!call.hidden) {
        call.href = telHref(selected.phone);
        call.textContent = `Call ${selected.city} showroom · ${selected.phone}`;
      }

      if (!this.modal.hidden) this.renderModal();
    }

    renderModal() {
      const stores = this.stores();
      const counts = { in: 0, low: 0, out: 0 };
      stores.forEach((s) => counts[s.status]++);
      const seeable = counts.in + counts.low;

      this.modal.querySelector("[data-sa-modal-sub]").textContent = `${this.dataset.productTitle} · ${this.variantLabel()}`;
      this.modal.querySelector("[data-sa-summary]").textContent = seeable
        ? `Available to see at ${seeable} of ${stores.length} showrooms`
        : "Not currently available at any showroom";

      const filters = [
        ["all", `All (${stores.length})`],
        ["in", `Available (${counts.in})`],
        ["low", `Limited (${counts.low})`],
        ["out", `Not available (${counts.out})`],
      ];
      this.modal.querySelector("[data-sa-filters]").innerHTML = filters
        .map(([id, label]) => `<button type="button" class="sa-pill" data-sa-filter="${id}" aria-pressed="${this.filter === id}">${label}</button>`)
        .join("");

      const rows = stores.filter((s) => this.filter === "all" || s.status === this.filter);
      this.modal.querySelector("[data-sa-rows]").innerHTML = rows.length
        ? rows.map((s) => this.rowHtml(s)).join("")
        : `<div class="sa-empty">No showrooms match this filter.</div>`;
    }

    rowHtml(s) {
      const status = STATUS[s.status];
      const maps = "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(s.address);
      const info = [
        ["Address", s.address],
        ["Hours", s.hours],
        ["Phone", s.phone],
      ]
        .filter(([, value]) => value)
        .map(([label, value]) => `<dt>${label}</dt><dd>${escapeHtml(value)}</dd>`)
        .join("");
      return `
        <div class="sa-row">
          <div class="sa-row__top">
            <div class="sa-row__city">${escapeHtml(s.city)}${s.region ? ", " + escapeHtml(s.region) : ""}</div>
            <div class="sa-badge" data-status="${s.status}">${status.label}</div>
          </div>
          <div class="sa-row__detail">${status.detail}</div>
          <dl class="sa-row__info">${info}</dl>
          <div class="sa-row__actions">
            ${s.phone ? `<a class="sa-btn sa-btn--call" href="${escapeHtml(telHref(s.phone))}">Call showroom</a>` : ""}
            <a class="sa-btn" href="${escapeHtml(maps)}" target="_blank" rel="noopener">Get directions</a>
          </div>
        </div>`;
    }

    openModal(trigger) {
      this.returnFocus = trigger;
      this.filter = "all";
      this.renderModal();
      this.modal.hidden = false;
      document.documentElement.style.overflow = "hidden";
      this.modal.querySelector("[data-sa-close]").focus();
    }

    closeModal() {
      this.modal.hidden = true;
      document.documentElement.style.overflow = "";
      if (this.returnFocus) this.returnFocus.focus();
    }

    onModalKeydown(e) {
      if (e.key === "Escape") {
        this.closeModal();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = [...this.modal.querySelectorAll("button, a[href]")];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define("showroom-availability", ShowroomAvailability);
})();
